import apiClient from './apiClient';
import { AxiosError } from 'axios';

// Types for responses
export interface BlindboxItem {
  name: string;
  value: number;
  probability: number;
}

export interface BlindboxPurchaseResult {
  item: BlindboxItem;
  systemFee: number;
  poolContribution: number;
  newPoolSize: number;
}

export interface PoolInfo {
  totalPoolSize: number;
  initialPoolSize: number;
  totalSystemFees: number;
  rewardPool: number;
  totalProviders: number;
  growth: number;
  state: 'Normal' | 'Adjusted';
}

export interface Transaction {
  id: string;
  type: string;
  address: string;
  amount: number;
  item?: BlindboxItem;
  timestamp: string;
  poolSizeAfter: number;
}

export interface BlindboxStats {
  poolInfo: PoolInfo;
  stats: {
    blindboxCost: number;
    systemFeePercentage: number;
    normalExpectedValue: number;
    adjustedExpectedValue: number;
    currentMode: string;
    rewardDistributionPercentage: number;
  };
  items: {
    normal: BlindboxItem[];
    adjusted: BlindboxItem[];
  };
  recentTransactions: Transaction[];
}

// Interface for item rate statistics
export interface ItemRateStats {
  name: string;
  value: number;
  count: number;
  actualRate: number;
  normalState: {
    count: number;
    actualRate: number;
    theoreticalRate: number;
    difference: number;
  };
  adjustedState: {
    count: number;
    actualRate: number;
    theoreticalRate: number;
    difference: number;
  };
}

// Interface for global rate statistics
export interface GlobalRateStats {
  totalPurchases: number;
  normalStateCount: number;
  adjustedStateCount: number;
  normalStatePct: number;
  adjustedStatePct: number;
  actualAverageReturn: number;
  theoreticalNormalEV: number;
  theoreticalAdjustedEV: number;
}

// Interface for the response from the actual rates endpoint
export interface BlindboxRatesResponse {
  globalStats: GlobalRateStats;
  itemStats: ItemRateStats[];
  totalTransactions: number;
}

// Blindbox service
const blindboxService = {
  // Purchase a blindbox
  purchaseBlindbox: async (walletAddress: string, quantity: number = 1): Promise<BlindboxPurchaseResult> => {
    console.log(`BlindboxService: Purchasing ${quantity} blindbox(es) for wallet`, walletAddress);
    try {
      console.log('Making POST request to /blindbox/purchase...');
      const response = await apiClient.post('/blindbox/purchase', { walletAddress, quantity });
      console.log('BlindboxService: Purchase response:', response.data);
      return response.data.data.blindbox;
    } catch (error: unknown) {
      console.error('Error purchasing blindbox:', error);
      // More detailed error logging
      if (error instanceof AxiosError) {
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error during request setup:', error.message);
        }
      }
      throw error;
    }
  },

  // Get blindbox statistics
  getBlindboxStats: async (): Promise<BlindboxStats> => {
    console.log('BlindboxService: Fetching blindbox stats...');
    try {
      console.log('Making GET request to /blindbox/stats...');
      const startTime = Date.now();
      const response = await apiClient.get('/blindbox/stats');
      const endTime = Date.now();
      console.log(`BlindboxService: Stats fetched in ${endTime - startTime}ms`);
      
      if (!response.data || !response.data.data) {
        console.error('BlindboxService: Invalid response format:', response);
        throw new Error('Invalid response format from API');
      }
      
      return response.data.data;
    } catch (error: unknown) {
      console.error('Error getting blindbox stats:', error);
      // More detailed error logging
      if (error instanceof AxiosError) {
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        } else if (error.request) {
          console.error('No response received. Backend might be down or unreachable.');
          console.error('Request details:', error.request);
        } else {
          console.error('Error during request setup:', error.message);
        }
      }
      throw error;
    }
  },
  
  // Get actual blindbox item rates based on historical data
  getBlindboxActualRates: async (): Promise<BlindboxRatesResponse> => {
    console.log('BlindboxService: Fetching actual blindbox rates...');
    try {
      const response = await apiClient.get('/blindbox/actual-rates');
      console.log('BlindboxService: Actual rates fetched successfully');
      
      if (!response.data || !response.data.data) {
        console.error('BlindboxService: Invalid response format:', response);
        throw new Error('Invalid response format from API');
      }
      
      return response.data.data;
    } catch (error: unknown) {
      console.error('Error getting actual blindbox rates:', error);
      if (error instanceof AxiosError) {
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        } else if (error.request) {
          console.error('No response received. Backend might be down or unreachable.');
        } else {
          console.error('Error during request setup:', error.message);
        }
      }
      throw error;
    }
  },
  
  // Test the API connection
  testConnection: async (): Promise<boolean> => {
    try {
      console.log('Testing API connection...');
      const response = await apiClient.get('/health');
      console.log('API connection test result:', response.data);
      return true;
    } catch (error: unknown) {
      console.error('API connection test failed:', error);
      return false;
    }
  }
};

export default blindboxService; 